import DOMPurify from 'dompurify';
import { ClipboardListIcon, MailIcon, MapPinIcon, PhoneIcon } from 'lucide-react';
import { Avatar, EmptyState, FactList, Panel, type Fact } from '@bcecloud/portal-ui';
import { useEventContext } from './useEventContext';
import { avatarSrc } from '@/lib/media';
import type { EventUser } from '@/types/event';

/**
 * Sanitizes the Salesforce-authored services HTML before rendering.
 *
 * The data is BCE-authored, but it flows through Salesforce where many hands
 * touch it — an allowlist of the tags the corpus actually uses (surveyed
 * across all ~7,800 events: p/br/span/i/li/strong/b/em/a/h2–h6/ul/ol/div,
 * plus the occasional dl, blockquote, table and sup) makes a pasted script
 * tag inert rather than executable. Inline `style` attributes are dropped on
 * purpose: Salesforce embeds its own colors and sizes, and the design
 * system's typography below should win.
 *
 * @param html - Raw eventServices HTML.
 * @returns Sanitized HTML safe to inject.
 */
function sanitizeServicesHtml(html: string): string {
  return DOMPurify.sanitize(html, {
    ALLOWED_TAGS: [
      'p', 'br', 'div', 'span', 'b', 'strong', 'i', 'em', 'u', 'sup', 'sub',
      'ul', 'ol', 'li', 'dl', 'dt', 'dd',
      'h1', 'h2', 'h3', 'h4', 'h5', 'h6',
      'a', 'blockquote', 'hr', 'table', 'tbody', 'thead', 'tr', 'td', 'th',
      'img', 'font',
    ],
    ALLOWED_ATTR: ['href', 'target', 'rel', 'src', 'alt'],
    ALLOWED_URI_REGEXP: /^https?:/i,
  });
}

/**
 * Formats a date string for display.
 *
 * @param dateStr - ISO date.
 * @returns e.g. "Friday, August 8, 2026".
 */
function formatDate(dateStr?: string): string {
  if (!dateStr) return '—';
  return new Date(dateStr).toLocaleDateString('en-US', {
    weekday: 'long',
    month: 'long',
    day: 'numeric',
    year: 'numeric',
  });
}

/**
 * Creates a display name from a user object.
 *
 * @param user - The BCE team member.
 * @returns Their name, or "Unknown".
 */
function getUserName(user: EventUser): string {
  return [user.firstName, user.lastName].filter(Boolean).join(' ') || 'Unknown';
}

/** One BCE person on the event, with how to reach them. */
interface TeamContact {
  readonly id: string;
  readonly name: string;
  readonly role: string;
  readonly email?: string | undefined;
  readonly phone?: string | undefined;
  readonly avatar?: string | undefined;
}

/**
 * Event overview — the facts, the services, the venue and the BCE team.
 */
export function EventOverviewTab(): React.ReactElement | null {
  const { event } = useEventContext();
  if (!event) return null;

  const venue = event.startVenue;

  const facts: Fact[] = [
    { label: 'Date', value: event.eventDate ? formatDate(event.eventDate) : null },
    {
      label: 'Time',
      value: event.timeStartString
        ? `${event.timeStartString}${event.timeEndString ? ` — ${event.timeEndString}` : ''}${event.timeZone ? ` (${event.timeZone})` : ''}`
        : null,
    },
    { label: 'Company', value: event.account?.name ?? null },
    { label: 'Program', value: event.programDisplayName ?? null },
    { label: 'Participants', value: event.participants ? String(event.participants) : null },
    { label: 'Status', value: event.stageName ?? null },
  ];

  const roleSlots: readonly { user: EventUser | undefined; role: string }[] = [
    { user: event.salesperson, role: 'Salesperson' },
    { user: event.leadFacilitator, role: 'Lead facilitator' },
    { user: event.programManager, role: 'Program manager' },
    { user: event.accountingContact, role: 'Payment specialist' },
    { user: event.productionContact, role: 'Production manager' },
    { user: event.contractManager, role: 'Contract manager' },
  ];
  const contacts: TeamContact[] = roleSlots.flatMap(({ user, role }) =>
    user
      ? [
          {
            id: user._id,
            name: getUserName(user),
            role,
            email: user.email,
            phone: user.phone,
            avatar: user.avatar,
          },
        ]
      : [],
  );

  const addressParts = venue
    ? [venue.street, [venue.city, venue.state].filter(Boolean).join(', '), venue.postalCode]
        .filter(Boolean)
        .join(' ')
    : '';
  const mapsUrl = venue
    ? `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent([venue.name, addressParts].filter(Boolean).join(', '))}`
    : '';

  return (
    <div className="grid gap-6 lg:grid-cols-3">
      <div className="space-y-6 lg:col-span-2">
        <Panel className="p-6">
          <h2 className="text-portal-section font-bold tracking-[-0.4px]">Event details</h2>
          <p className="mt-1 text-portal-tiny text-portal-muted">
            Your event information at a glance.
          </p>
          <div className="mt-4">
            <FactList facts={facts} />
          </div>
        </Panel>

        <Panel className="p-6">
          <h2 className="text-portal-section font-bold tracking-[-0.4px]">Event services</h2>
          <p className="mt-1 text-portal-tiny text-portal-muted">
            What&rsquo;s included in this event.
          </p>
          <div className="mt-4">
            {event.eventServices && !event.eventServices.startsWith('ERROR') ? (
              <div
                // Salesforce-authored HTML uses a small vocabulary — p, br,
                // b/strong, i/em, u, span — with section titles written as a
                // bold-only paragraph (<p><b>Program Agenda</b></p>). The
                // :only-child rules promote exactly those into real headings;
                // list/heading tags are covered defensively should richer
                // content ever arrive.
                className={[
                  'max-w-none text-portal-small leading-[1.7] text-portal-ink',
                  '[&_p]:mb-3',
                  '[&_b]:font-bold [&_strong]:font-bold',
                  '[&_i]:italic [&_em]:italic [&_u]:underline',
                  // A paragraph that is nothing but one bold run is a section
                  // title — give it heading scale and breathing room.
                  '[&_p>b:only-child]:block [&_p>b:only-child]:text-portal-card-title [&_p>b:only-child]:font-bold [&_p>b:only-child]:tracking-[-0.2px]',
                  '[&_p:has(>b:only-child)]:mt-5 [&_p:has(>b:only-child)]:mb-2',
                  '[&_p:has(>b:only-child):first-child]:mt-0',
                  // Headings — h3 is what most program copy actually uses.
                  '[&_h1]:mt-5 [&_h1]:mb-2 [&_h1]:text-portal-section [&_h1]:font-bold',
                  '[&_h2]:mt-5 [&_h2]:mb-2 [&_h2]:text-portal-card-title [&_h2]:font-bold',
                  '[&_h3]:mt-5 [&_h3]:mb-2 [&_h3]:text-portal-card-title [&_h3]:font-bold [&_h3]:tracking-[-0.2px]',
                  '[&_h4]:mt-4 [&_h4]:mb-1.5 [&_h4]:text-portal-small [&_h4]:font-bold',
                  '[&_h5]:mt-4 [&_h5]:mb-1.5 [&_h5]:text-portal-small [&_h5]:font-bold',
                  '[&_h6]:mt-4 [&_h6]:mb-1.5 [&_h6]:text-portal-small [&_h6]:font-bold',
                  // Lists — preflight strips markers; restore proper bullets.
                  '[&_ul]:mb-3 [&_ul]:list-disc [&_ul]:pl-5 [&_ol]:mb-3 [&_ol]:list-decimal [&_ol]:pl-5',
                  '[&_li]:mb-1.5 [&_li]:pl-1 marker:[&_li]:text-portal-muted',
                  '[&_dl]:mb-3 [&_dt]:font-bold [&_dd]:mb-1.5 [&_dd]:pl-5',
                  // The stragglers.
                  '[&_a]:font-bold [&_a]:text-brand-link [&_a]:underline-offset-2 hover:[&_a]:underline',
                  '[&_blockquote]:my-3 [&_blockquote]:border-l-2 [&_blockquote]:border-portal-line [&_blockquote]:pl-4 [&_blockquote]:text-portal-muted',
                  '[&_hr]:my-4 [&_hr]:border-portal-line',
                  '[&_table]:mb-3 [&_table]:w-full [&_td]:border [&_td]:border-portal-line [&_td]:p-2 [&_th]:border [&_th]:border-portal-line [&_th]:p-2 [&_th]:font-bold',
                  '[&_img]:my-3 [&_img]:max-w-full [&_img]:rounded-portal-button',
                ].join(' ')}
                dangerouslySetInnerHTML={{ __html: sanitizeServicesHtml(event.eventServices) }}
              />
            ) : (
              <EmptyState
                icon={<ClipboardListIcon aria-hidden className="size-6" />}
                title="No event services listed yet"
                message="Your BCE team adds the service details as the event is planned."
              />
            )}
          </div>
        </Panel>
      </div>

      <div className="space-y-6">
        {venue && (venue.name || venue.city) ? (
          <Panel className="p-6">
            <h2 className="text-portal-section font-bold tracking-[-0.4px]">Venue</h2>
            <p className="mt-1 text-portal-tiny text-portal-muted">
              Where this event takes place.
            </p>

            <a
              href={mapsUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="group mt-4 flex items-start gap-3"
            >
              <MapPinIcon aria-hidden className="mt-0.5 size-4 shrink-0 text-brand" />
              <span>
                {venue.name ? (
                  <span className="block text-portal-small font-bold group-hover:text-brand-link">
                    {venue.name}
                  </span>
                ) : null}
                {venue.street ? (
                  <span className="block text-portal-small text-portal-muted">{venue.street}</span>
                ) : null}
                {venue.city || venue.state || venue.postalCode ? (
                  <span className="block text-portal-small text-portal-muted">
                    {[venue.city, venue.state].filter(Boolean).join(', ')}
                    {venue.postalCode ? ` ${venue.postalCode}` : ''}
                  </span>
                ) : null}
                <span className="mt-1 block text-portal-micro font-bold text-brand-link opacity-0 transition-opacity group-hover:opacity-100">
                  Open in Google Maps →
                </span>
              </span>
            </a>

            {venue.room ? (
              <div className="mt-4 rounded-portal-button bg-portal-bg px-3 py-2">
                <span className="block text-portal-micro font-bold text-portal-muted">Room</span>
                <span className="block text-portal-small">{venue.room}</span>
              </div>
            ) : null}

            {venue.phone ? (
              <div className="mt-2 rounded-portal-button bg-portal-bg px-3 py-2">
                <span className="block text-portal-micro font-bold text-portal-muted">
                  Venue phone
                </span>
                <a
                  href={`tel:${venue.phone}`}
                  className="text-portal-small text-brand-link hover:underline"
                >
                  {venue.phone}
                </a>
              </div>
            ) : null}
          </Panel>
        ) : null}

        {contacts.length > 0 ? (
          <Panel className="p-6">
            <h2 className="text-portal-section font-bold tracking-[-0.4px]">Your BCE team</h2>
            <p className="mt-1 text-portal-tiny text-portal-muted">
              The people delivering this event.
            </p>
            <ul className="mt-4 space-y-3">
              {contacts.map((contact) => {
                const imageUrl = avatarSrc({ _id: contact.id, avatar: contact.avatar });
                return (
                  <li
                    key={`${contact.id}-${contact.role}`}
                    className="flex items-start gap-3 rounded-portal-button border border-portal-line p-3.5"
                  >
                    <Avatar name={contact.name} {...(imageUrl ? { imageUrl } : {})} />
                    <span className="min-w-0 space-y-1">
                      <span className="block">
                        <span className="block text-portal-small font-bold">{contact.name}</span>
                        <span className="block text-portal-micro text-portal-muted">
                          {contact.role}
                        </span>
                      </span>
                      {contact.email ? (
                        <a
                          href={`mailto:${contact.email}`}
                          className="flex items-center gap-1.5 text-portal-micro font-bold text-brand-link hover:underline"
                        >
                          <MailIcon aria-hidden className="size-3.5 shrink-0" />
                          <span className="break-all">{contact.email}</span>
                        </a>
                      ) : null}
                      {contact.phone ? (
                        <a
                          href={`tel:${contact.phone}`}
                          className="flex items-center gap-1.5 text-portal-micro text-portal-muted hover:underline"
                        >
                          <PhoneIcon aria-hidden className="size-3.5 shrink-0" />
                          {contact.phone}
                        </a>
                      ) : null}
                    </span>
                  </li>
                );
              })}
            </ul>
          </Panel>
        ) : null}
      </div>
    </div>
  );
}
